import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useGlobalLoading } from '../hooks/useGlobalLoading';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const textTransition = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
`;

const LoaderContainer = styled.div<{ $fullscreen?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease-in-out;
  
  ${props => props.$fullscreen && `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--color-background);
    z-index: 10000;
  `}
  
  ${props => !props.$fullscreen && `
    padding: 40px 20px;
  `}
`;

const Spinner = styled.div`
  border: 3px solid rgba(96, 159, 148, 0.2);
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  width: 40px;
  height: 40px;
`;

const LoaderText = styled.div<{ $isChanging: boolean }>`
  margin-top: 16px;
  color: var(--color-text-secondary);
  font-family: var(--font-family-main);
  font-weight: 400;
  text-align: center;
  font-size: 14px;
  transition: opacity 0.3s ease-in-out;
  animation: ${props => props.$isChanging ? textTransition : 'none'} 0.6s ease-in-out;
`;

interface LoadingManagerProps {
  fullscreen?: boolean;
}

const LoadingManager: React.FC<LoadingManagerProps> = ({ 
  fullscreen = false 
}) => {
  const { activeSteps, hasActiveSteps } = useGlobalLoading();
  const [currentStep, setCurrentStep] = useState<{ id: string; text: string } | null>(null);
  const [isChangingText, setIsChangingText] = useState(false);

  useEffect(() => {
    if (!hasActiveSteps) {
      if (currentStep) {
        setTimeout(() => {
          setCurrentStep(null);
        }, 200);
      }
      return;
    }

    const newStep = activeSteps[0];
    
    if (currentStep && currentStep.id !== newStep.id) {
      setIsChangingText(true);
      
      setTimeout(() => {
        setCurrentStep(newStep);
        setIsChangingText(false);
      }, 300);
    } else if (!currentStep) {
      setCurrentStep(newStep);
    }
  }, [activeSteps, hasActiveSteps, currentStep]);

  if (!hasActiveSteps || !currentStep) {
    return null;
  }

  return (
    <LoaderContainer $fullscreen={fullscreen}>
      <Spinner />
      <LoaderText $isChanging={isChangingText}>
        {currentStep.text}
      </LoaderText>
    </LoaderContainer>
  );
};

export default LoadingManager; 